import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt, faSyncAlt, faEye, faDownload, faPencilAlt, faTrash, faPlus,
  faSpinner, faTimes, faUpload, faExclamationTriangle, faSearch, faShieldAlt,
  faCheckCircle, faClock, faExclamationCircle, faFolderOpen, faMapPin,
  faDrawPolygon, faChevronLeft, faChevronRight as faChevR, faLayerGroup,
  faList, faCubes, faCloudUploadAlt, faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import * as FaIcons from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { getAuthHeader } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import { sortNewestFirst, insertNewestFirst } from '../../../utils/sortUtils';
import DocumentsPreviewPanel from './DocumentsPreviewPanel';
import DocumentsCategoriesAdmin from './DocumentsCategoriesAdmin';
import './Documents.css';

/* ── Helpers ─────────────────────────────────────────────── */
const EXPIRY_SOON_DAYS = 30;
const PAGE_SIZE = 50;            // virtualisation: simple "load more" pagination
const SEARCH_DEBOUNCE_MS = 250;

const FALLBACK_CATEGORY_ICONS = {
  'Fire Safety': '🔥', 'Electrical': '⚡', 'Roof Safety': '🏗️',
  'Building Maintenance': '🔧', 'Equipment Safety': '⚙️', 'Staff Safety': '👷',
  'Emergency Preparedness': '🚨', 'Insurance & Certifications': '📋',
  'Inspection Reports': '🔍', 'Risk Assessments': '⚠️', 'Gas Safety': '🔥',
  'Water Hygiene': '💧', 'Asbestos': '☣️', 'Drawings': '📐',
  'Test Data Sheets': '📊', 'Other': '📁',
};

const iconForName = (name) => {
  if (!name) return FaIcons.faFolderOpen;
  const studly = 'fa' + name.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
  return FaIcons[studly] || FaIcons.faFolderOpen;
};

const getComputedStatus = (doc) => {
  if (!doc?.expiry_date) return doc?.status || 'Active';
  const expiry = new Date(doc.expiry_date);
  if (isNaN(expiry.getTime())) return doc.status || 'Active';
  const now = new Date();
  if (expiry < now) return 'Expired';
  const limit = new Date();
  limit.setDate(limit.getDate() + EXPIRY_SOON_DAYS);
  if (expiry <= limit) return 'Expiring Soon';
  return doc.status || 'Active';
};

const daysUntilExpiry = (doc) => {
  if (!doc?.expiry_date) return null;
  const expiry = new Date(doc.expiry_date);
  if (isNaN(expiry.getTime())) return null;
  return Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24));
};

const expiryLabel = (doc) => {
  const days = daysUntilExpiry(doc);
  if (days === null) return null;
  if (days < 0) return `Expired ${Math.abs(days)}d ago`;
  if (days === 0) return 'Expires today';
  if (days <= EXPIRY_SOON_DAYS) return `Expires in ${days}d`;
  return 'Valid';
};

const friendlySize = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const friendlyAssetType = (raw) =>
  (raw || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/* ── Component ───────────────────────────────────────────── */
export default function Documents() {
  const { id: propertyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /* Core data */
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  /* Toolbar state */
  const [viewMode, setViewMode] = useState('document');     // 'document' | 'asset'
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');                 // debounced
  const [activeCategoryIds, setActiveCategoryIds] = useState(() => new Set());
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [page, setPage] = useState(1);

  /* Selection (bulk) */
  const [selected, setSelected] = useState(() => new Set());

  /* Drag-drop */
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);

  /* Side panel preview */
  const [previewDoc, setPreviewDoc] = useState(null);

  /* Modals */
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '', categoryIds: [], type: '', zone: '', summary: '', expiryDate: '', file: null,
    customZone: '',
  });
  const [uploadError, setUploadError] = useState('');
  const [uploadStep, setUploadStep] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showPinpointModal, setShowPinpointModal] = useState(false);
  const [pinpointDoc, setPinpointDoc] = useState(null);
  const [pinpointForm, setPinpointForm] = useState({ name: '', category: 'Roof Safety', description: '' });
  const [convertingToPinpoint, setConvertingToPinpoint] = useState(false);

  /* Move-to-category modal (bulk action) */
  const [showMoveModal, setShowMoveModal] = useState(false);

  /* ── Search debounce ─────────────────────────────────── */
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ── Fetch ───────────────────────────────────────────── */
  const fetchProperty = useCallback(async () => {
    if (!propertyId) return;
    try {
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}`), { headers: getAuthHeader() });
      if (!r.ok) throw new Error();
      setProperty(await r.json());
    } catch (_) { /* property header is optional */ }
  }, [propertyId]);

  const fetchDocuments = useCallback(async () => {
    if (!propertyId) { setError('No property selected.'); setLoading(false); return; }
    try {
      setLoading(true);
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/documents`), { headers: getAuthHeader() });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setDocuments(sortNewestFirst(data.documents || []));
      setError(null);
    } catch (_) {
      setError('Failed to load documents');
      toast.error('Failed to load documents');
    } finally { setLoading(false); }
  }, [propertyId]);

  const fetchCategories = useCallback(async () => {
    try {
      const r = await fetch(getApiEndpoint('/api/document-categories'), { headers: getAuthHeader() });
      if (!r.ok) throw new Error();
      const data = await r.json();
      const raw = Array.isArray(data?.categories) ? data.categories : [];
      const seenNames = new Set();
      setCategories(raw.filter(c => { if (seenNames.has(c.name)) return false; seenNames.add(c.name); return true; }));
    } catch (_) { /* categories are non-blocking */ }
  }, []);

  useEffect(() => {
    if (propertyId) { fetchProperty(); fetchDocuments(); }
    fetchCategories();
    fetch(getApiEndpoint('/api/users/me'), { headers: getAuthHeader() })
      .then(r => r.ok ? r.json() : null)
      .then(me => { if (me?.id) setCurrentUserId(me.id); })
      .catch(() => {});
  }, [propertyId, fetchProperty, fetchDocuments, fetchCategories]);

  /* ── Status filter from URL (?status=expiring) ───────── */
  useEffect(() => {
    const fromUrl = searchParams.get('status');
    if (fromUrl && fromUrl !== statusFilter) setStatusFilter(fromUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ── Derived data ────────────────────────────────────── */
  const stats = useMemo(() => {
    let active = 0, expired = 0, expSoon = 0;
    for (const d of documents) {
      const s = getComputedStatus(d);
      if (s === 'Expired') expired++;
      else if (s === 'Expiring Soon') expSoon++;
      else active++;
    }
    const total = documents.length;
    const compliant = total > 0 ? Math.round(((active + expSoon) / total) * 100) : 100;
    return { total, active, expired, expSoon, compliant };
  }, [documents]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    const catIds = activeCategoryIds;
    const catNames = new Set(
      Array.from(catIds).map(id => categories.find(c => c.id === id)?.name).filter(Boolean)
    );
    return documents.filter(d => {
      if (catIds.size > 0) {
        const matchesCat = (d.category_id && catIds.has(d.category_id)) ||
                           (d.type && catNames.has(d.type));
        if (!matchesCat) return false;
      }
      if (statusFilter) {
        const s = getComputedStatus(d);
        if (statusFilter === 'valid' && s !== 'Active') return false;
        if (statusFilter === 'expiring' && s !== 'Expiring Soon') return false;
        if (statusFilter === 'expired' && s !== 'Expired') return false;
      }
      if (term) {
        const blob = [
          d.name, d.original_filename, d.summary, d.zone, d.uploaded_by_name, d.type
        ].filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(term)) return false;
      }
      return true;
    });
  }, [documents, activeCategoryIds, categories, statusFilter, search]);

  /* By-asset grouping (for the alternate view) */
  const byAssetGroups = useMemo(() => {
    const groups = new Map();
    const buildingWide = { key: '__building__', label: 'Building wide', rows: [] };
    for (const d of filtered) {
      if (d.asset_type && d.asset_id) {
        const key = `${d.asset_type}::${d.asset_id}`;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            label: friendlyAssetType(d.asset_type),
            assetId: d.asset_id,
            rows: [],
          });
        }
        groups.get(key).rows.push(d);
      } else {
        buildingWide.rows.push(d);
      }
    }
    const arr = Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label));
    if (buildingWide.rows.length > 0) arr.unshift(buildingWide);
    return arr;
  }, [filtered]);

  /* Unique floor/zone values — from documents state + dedicated API */
  const [propertyZones, setPropertyZones] = useState([]);
  useEffect(() => {
    if (!propertyId) return;
    const seen = new Set();
    const addZones = (vals) => { vals.forEach(z => { if (z?.trim()) seen.add(z.trim()); }); };
    Promise.allSettled([
      fetch(getApiEndpoint(`/api/properties/${propertyId}/zones`), { headers: getAuthHeader() })
        .then(r => r.ok ? r.json() : { zones: [] })
        .then(d => addZones(d.zones || [])),
      fetch(getApiEndpoint(`/api/onboarding-wizard/properties/${propertyId}/sessions`), { headers: getAuthHeader() })
        .then(r => r.ok ? r.json() : { sessions: [] })
        .then(d => addZones((d.sessions || []).map(s => s.level_name).filter(Boolean))),
    ]).then(() => setPropertyZones(Array.from(seen).sort()));
  }, [propertyId]);

  const existingZones = useMemo(() => {
    const seen = new Set(propertyZones);
    for (const d of documents) {
      if (d.zone && d.zone.trim()) seen.add(d.zone.trim());
    }
    return Array.from(seen).sort();
  }, [documents, propertyZones]);

  /* Pagination for the flat list */
  const visibleDocs = useMemo(() => filtered.slice(0, page * PAGE_SIZE), [filtered, page]);
  const hasMore = filtered.length > visibleDocs.length;

  /* Reset page when filter changes meaningfully */
  useEffect(() => { setPage(1); }, [activeCategoryIds, statusFilter, search]);

  /* Reset selection when filter changes */
  useEffect(() => { setSelected(new Set()); }, [activeCategoryIds, statusFilter, search, viewMode]);

  /* Accordion: categories with documents, each containing floor groups */
  const categoryAccordions = useMemo(() => {
    const catIds = new Set(categories.map(c => c.id));
    const catNames = new Set(categories.map(c => c.name));
    const matched = new Set();

    const accordions = categories
      .map(cat => {
        const docs = documents.filter(d =>
          (d.category_id && d.category_id === cat.id) || d.type === cat.name
        );
        if (docs.length === 0) return null;
        docs.forEach(d => matched.add(d.id));
        const zoneMap = new Map();
        for (const d of docs) {
          const zone = d.zone?.trim() || '__building__';
          if (!zoneMap.has(zone)) zoneMap.set(zone, []);
          zoneMap.get(zone).push(d);
        }
        const zones = Array.from(zoneMap.entries())
          .sort(([a], [b]) => {
            if (a === '__building__') return 1;
            if (b === '__building__') return -1;
            return a.localeCompare(b);
          })
          .map(([zone, zoneDocs]) => ({ zone, docs: zoneDocs }));
        return { cat, docs, zones };
      })
      .filter(Boolean);

    // Docs not matched by a known category — group by their type field
    const uncatDocs = documents.filter(d => !matched.has(d.id));
    if (uncatDocs.length > 0) {
      const typeGroups = new Map();
      for (const d of uncatDocs) {
        const label = d.type || 'Uncategorised';
        if (!typeGroups.has(label)) typeGroups.set(label, []);
        typeGroups.get(label).push(d);
      }
      for (const [label, groupDocs] of typeGroups) {
        accordions.push({
          cat: { id: `__type__${label}`, name: label, icon: 'folder-open', colour: '#6b7280' },
          docs: groupDocs,
          zones: [{ zone: '__building__', docs: groupDocs }],
        });
      }
    }

    return accordions;
  }, [categories, documents]);

  const [openCategories, setOpenCategories] = useState(() => new Set());
  const toggleCategoryAccordion = (catId) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId); else next.add(catId);
      return next;
    });
  };

  const [openZones, setOpenZones] = useState(() => new Set());
  const toggleZone = (key) => {
    setOpenZones(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  /* ── Toolbar handlers ───────────────────────────────── */
  const toggleCategory = (id) => {
    setActiveCategoryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearCategories = () => setActiveCategoryIds(new Set());
  const setStatus = (s) => {
    setStatusFilter(s);
    if (s) setSearchParams(prev => { prev.set('status', s); return prev; }, { replace: true });
    else setSearchParams(prev => { prev.delete('status'); return prev; }, { replace: true });
  };

  /* ── Drag-drop ──────────────────────────────────────── */
  const onDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current += 1;
    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setDragActive(false);
  };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    setUploadForm(prev => ({
      ...prev,
      file,
      name: prev.name || file.name.replace(/\.[^.]+$/, ''),
    }));
    setShowUploadModal(true);
  };

  /* ── Upload / edit / delete ─────────────────────────── */
  const handleUpload = async (e) => {
    e?.preventDefault();
    setUploadError('');
    if (!uploadForm.file) { setUploadError('Please select a file'); return; }
    if (!uploadForm.name) { setUploadError('Please enter a document name'); return; }
    if (uploadForm.categoryIds.length === 0) { setUploadError('Please pick a category'); return; }
    const fd = new FormData();
    const primaryCatId = uploadForm.categoryIds[0];
    const primaryCat = categories.find(c => c.id === primaryCatId);
    fd.append('file', uploadForm.file);
    fd.append('name', uploadForm.name);
    fd.append('type', primaryCat?.name || uploadForm.type || '');
    if (primaryCatId) fd.append('category_id', String(primaryCatId));
    fd.append('zone', uploadForm.zone || '');
    fd.append('summary', uploadForm.summary || '');
    const expiryToSend = uploadForm.expiryDate === 'N/A' ? '' : uploadForm.expiryDate;
    if (expiryToSend) fd.append('expiryDate', expiryToSend);
    try {
      setUploading(true);
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/documents`), {
        method: 'POST', headers: getAuthHeader(), body: fd,
      });
      if (!r.ok && r.status !== 202) throw new Error('Failed to upload document');
      const data = await r.json();
      const queuedOffline = r.status === 202 || data?.queued === true;
      const docToShow = queuedOffline ? {
        id: `pending-${Date.now()}`,
        name: uploadForm.name,
        original_filename: uploadForm.file?.name,
        type: uploadForm.type,
        zone: uploadForm.zone || '',
        summary: uploadForm.summary || '',
        expiry_date: uploadForm.expiryDate || null,
        mime_type: uploadForm.file?.type,
        file_size: uploadForm.file?.size,
        status: 'Pending sync',
        created_at: new Date().toISOString(),
        _pending: true,
      } : data.document;
      if (docToShow) setDocuments(prev => insertNewestFirst(prev, docToShow));
      setShowUploadModal(false);
      setUploadStep(1);
      setUploadForm({ name: '', categoryIds: [], type: '', zone: '', summary: '', expiryDate: '', file: null, customZone: '' });
      toast.success(queuedOffline
        ? 'Saved offline — will sync when you\'re back online'
        : 'Document uploaded');
      fetchCategories(); // counts may have shifted
    } catch (_) {
      toast.error('Failed to upload document');
    } finally { setUploading(false); }
  };

  const handleDownload = async (doc) => {
    // Contractor-linked docs (two-way link) are read-only mirrors — the binary lives under
    // the shared uploads path, so download straight from download_url rather than the FM route.
    if (doc.source === 'contractor' && doc.download_url) {
      try {
        const r = await fetch(getApiEndpoint(doc.download_url), { headers: getAuthHeader() });
        if (!r.ok) throw new Error();
        const blob = await r.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none'; a.href = url; a.download = doc.original_filename || doc.name;
        document.body.appendChild(a); a.click();
        window.URL.revokeObjectURL(url); document.body.removeChild(a);
      } catch (_) { toast.error('Failed to download document'); }
      return;
    }
    try {
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/documents/${doc.id}/download`), {
        headers: getAuthHeader(),
      });
      if (r.status === 410) { toast.error('File missing — please re-upload'); return; }
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none'; a.href = url; a.download = doc.original_filename || doc.name;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (_) { toast.error('Failed to download document'); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try {
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/documents/${doc.id}`), {
        method: 'DELETE', headers: getAuthHeader(),
      });
      if (!r.ok) throw new Error();
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      if (previewDoc?.id === doc.id) setPreviewDoc(null);
      toast.success('Document deleted');
      fetchCategories();
    } catch (_) { toast.error('Failed to delete document'); }
  };

  const handleUpdateDocument = async (e) => {
    e?.preventDefault();
    try {
      const cat = categories.find(c => c.id === editingDocument.category_id);
      const payload = {
        name: editingDocument.name,
        type: cat?.name || editingDocument.type,
        category_id: editingDocument.category_id || null,
        zone: editingDocument.zone || '',
        summary: editingDocument.summary || '',
        status: editingDocument.status,
        expiryDate: editingDocument.expiry_date || null,
      };
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/documents/${editingDocument.id}`), {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setDocuments(prev => prev.map(d => d.id === editingDocument.id ? { ...d, ...data.document } : d));
      setShowEditModal(false); setEditingDocument(null);
      toast.success('Document updated');
      fetchCategories();
    } catch (_) { toast.error('Failed to update document'); }
  };

  /* ── Pinpoint conversion (preserved from old) ────────── */
  const handleViewPinpoint = async (doc) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}/pinpoint/check/${doc.id}`), { headers: getAuthHeader() });
      const data = await res.json();
      if (data.isPinpoint) navigate(`/propertydashboard/${propertyId}/pinpoint/${data.pinpointDocument.id}`);
      else {
        const isImage = doc.mime_type && doc.mime_type.startsWith('image/');
        if (!isImage) { toast.error('Only image files (JPG, PNG) can be converted to pinpoint drawings'); return; }
        setPinpointDoc(doc);
        setPinpointForm({ name: doc.name, category: 'Roof Safety', description: '' });
        setShowPinpointModal(true);
      }
    } catch { toast.error('Failed to check pinpoint status'); }
  };

  const handleConvertToPinpoint = async (e) => {
    e?.preventDefault();
    setConvertingToPinpoint(true);
    try {
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/pinpoint`), {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId: pinpointDoc.id, name: pinpointForm.name,
          category: pinpointForm.category, description: pinpointForm.description,
        }),
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      toast.success('Document converted to pinpoint drawing');
      setShowPinpointModal(false);
      navigate(`/propertydashboard/${propertyId}/pinpoint/${data.pinpointDocument.id}`);
    } catch (_) {
      toast.error('Failed to convert document');
    } finally { setConvertingToPinpoint(false); }
  };

  /* ── Bulk actions ───────────────────────────────────── */
  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllOnPage = () => setSelected(new Set(visibleDocs.map(d => d.id)));
  const clearSelection = () => setSelected(new Set());

  const bulkDownload = async () => {
    // Trigger downloads sequentially with a tiny delay so the browser doesn't suppress them.
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    for (const id of ids) {
      const doc = documents.find(d => d.id === id);
      if (!doc) continue;
      // eslint-disable-next-line no-await-in-loop
      await handleDownload(doc);
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, 250));
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} document${selected.size === 1 ? '' : 's'}?`)) return;
    try {
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/documents/bulk`), {
        method: 'DELETE',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Failed');
      const deletedIds = new Set(data.ids || []);
      setDocuments(prev => prev.filter(d => !deletedIds.has(d.id)));
      setSelected(new Set());
      toast.success(`Deleted ${data.deleted || deletedIds.size} document${(data.deleted || deletedIds.size) === 1 ? '' : 's'}`);
      fetchCategories();
    } catch (err) { toast.error(err.message || 'Failed to delete documents'); }
  };

  const bulkMoveToCategory = async (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return;
    try {
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/documents/bulk-category`), {
        method: 'PATCH',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selected), category_id: cat.id, type: cat.name }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Failed');
      const updatedIds = new Set(Array.from(selected));
      setDocuments(prev => prev.map(d => updatedIds.has(d.id)
        ? { ...d, category_id: cat.id, type: cat.name }
        : d
      ));
      setShowMoveModal(false);
      setSelected(new Set());
      toast.success(`Moved ${data.updated || updatedIds.size} doc${(data.updated || updatedIds.size) === 1 ? '' : 's'} to ${cat.name}`);
      fetchCategories();
    } catch (err) { toast.error(err.message || 'Failed to move documents'); }
  };

  /* ── Open the upload modal pre-populated with category ── */
  const openUploadFor = (cat) => {
    setUploadForm(prev => ({ ...prev, type: cat?.name || '', categoryIds: cat?.id ? [cat.id] : [] }));
    setShowUploadModal(true);
  };

  /* ── Sub-components ────────────────────────────────── */
  const StatusBadge = ({ status }) => {
    const tone = status === 'Expired' ? 'tone-red'
              : status === 'Expiring Soon' ? 'tone-amber'
              : status === 'Active' ? 'tone-green'
              : 'tone-grey';
    const icon = status === 'Expired' ? faExclamationCircle
              : status === 'Expiring Soon' ? faClock
              : status === 'Active' ? faCheckCircle
              : faFileAlt;
    return (
      <span className={`docs-badge ${tone}`}>
        <FontAwesomeIcon icon={icon} /> {status}
      </span>
    );
  };

  const CategoryChip = ({ cat, count, active, onClick, dim }) => {
    const colour = cat.colour || '#6b7280';
    return (
      <button
        type="button"
        className={`docs-chip ${active ? 'active' : ''} ${dim ? 'dim' : ''}`}
        style={{ '--chip-color': colour }}
        onClick={onClick}
      >
        <span className="docs-chip-icon">
          <FontAwesomeIcon icon={iconForName(cat.icon)} />
        </span>
        <span className="docs-chip-label">{cat.name}</span>
        {count > 0 && <span className="docs-chip-count">{count}</span>}
      </button>
    );
  };

  const DocumentRow = ({ doc, isOwner, isSelected }) => {
    const cs = getComputedStatus(doc);
    const expLabel = expiryLabel(doc);
    const expTone = cs === 'Expired' ? 'tone-red' : cs === 'Expiring Soon' ? 'tone-amber' : 'tone-grey';
    const cat = categories.find(c => (doc.category_id && c.id === doc.category_id) || c.name === doc.type);
    const colour = cat?.colour || '#6b7280';
    const isPreviewing = previewDoc?.id === doc.id;
    const isContractor = doc.source === 'contractor' || doc.read_only;
    return (
      <div
        className={`docs-row ${isPreviewing ? 'previewing' : ''} ${isSelected ? 'selected' : ''} ${cs === 'Expired' ? 'is-expired' : cs === 'Expiring Soon' ? 'is-expiring' : ''}`}
        onClick={() => setPreviewDoc(doc)}
        role="button"
        tabIndex={0}
      >
        {isContractor ? (
          <span className="docs-row-check" aria-hidden="true" />
        ) : (
          <label className="docs-row-check" onClick={e => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelect(doc.id)}
              aria-label={`Select ${doc.name}`}
            />
          </label>
        )}
        <div className="docs-row-icon" style={{ '--row-color': colour }}>
          {cat?.icon
            ? <FontAwesomeIcon icon={iconForName(cat.icon)} />
            : <span className="docs-row-icon-emoji">{FALLBACK_CATEGORY_ICONS[doc.type] || '📁'}</span>}
        </div>
        <div className="docs-row-main">
          <div className="docs-row-name">{doc.name}</div>
          <div className="docs-row-meta">
            <span className="docs-row-cat">{doc.type || 'Other'}</span>
            {isContractor && (
              <span
                className="docs-row-contractor-tag"
                title="Uploaded by the contractor — read-only here"
                style={{
                  marginLeft: 8, padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                  letterSpacing: '.02em', background: 'rgba(37,99,235,.12)', color: '#1d4ed8',
                  border: '1px solid rgba(37,99,235,.25)', whiteSpace: 'nowrap',
                }}
              >
                {doc.source_ref || 'Contractor'}
              </span>
            )}
            {doc.zone && <><span className="docs-sep">·</span><span>{doc.zone}</span></>}
            {doc.original_filename && <><span className="docs-sep">·</span><span className="docs-row-fn" title={doc.original_filename}>{doc.original_filename}</span></>}
            {doc.uploaded_by_name && <><span className="docs-sep">·</span><span>{doc.uploaded_by_name}</span></>}
          </div>
        </div>
        <div className="docs-row-status">
          <StatusBadge status={cs} />
          {expLabel && cs !== 'Active' && (
            <span className={`docs-expiry-pill ${expTone}`}>{expLabel}</span>
          )}
        </div>
        <div className="docs-row-actions" onClick={e => e.stopPropagation()}>
          {doc.pinpoint_document_id && (
            <button
              className="docs-row-btn docs-row-btn-drawing"
              title="View as interactive drawing"
              onClick={() => navigate(`../drawings?open=${doc.id}`)}
            >
              <FontAwesomeIcon icon={faDrawPolygon} />
            </button>
          )}
          <button className="docs-row-btn" title="Download" onClick={() => handleDownload(doc)}>
            <FontAwesomeIcon icon={faDownload} />
          </button>
          {isOwner && (
            <button className="docs-row-btn" title="Edit" onClick={() => {
              const cat2 = categories.find(c => (doc.category_id && c.id === doc.category_id) || c.name === doc.type);
              setEditingDocument({ ...doc, category_id: cat2?.id || null });
              setShowEditModal(true);
            }}>
              <FontAwesomeIcon icon={faPencilAlt} />
            </button>
          )}
          {isOwner && (
            <button className="docs-row-btn danger" title="Delete" onClick={() => handleDelete(doc)}>
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const propertyName = property?.name || (propertyId ? 'Loading…' : 'All Properties');
  const propertyAddress = property?.address || '';
  const canManageCats = true; // server enforces; UI lets everyone open the panel and try.

  /* ── Loading / error ─────────────────────────────────── */
  if (loading) return (
    <div className="docs-page">
      <div className="docs-content">
        <div className="docs-loading-state">
          <FontAwesomeIcon icon={faSpinner} spin className="docs-loading-icon" />
          <div>Loading documents…</div>
        </div>
      </div>
    </div>
  );

  if (error && !property && documents.length === 0) return (
    <div className="docs-page">
      <div className="docs-content">
        <div className="docs-error-state">
          <FontAwesomeIcon icon={faExclamationTriangle} className="docs-error-icon" />
          <div>{error}</div>
          <button className="docs-retry-btn" onClick={() => { fetchProperty(); fetchDocuments(); }}>Retry</button>
        </div>
      </div>
    </div>
  );

  /* ── Render ──────────────────────────────────────────── */
  return (
    <div
      className={`docs-page ${dragActive ? 'drag-active' : ''}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="docs-content">

        {/* ── Sticky page banner: expiring/expired docs (filterable) ── */}
        {(stats.expired > 0 || stats.expSoon > 0) && (
          <div className={`docs-page-banner ${stats.expired > 0 ? 'tone-red' : 'tone-amber'}`}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="docs-page-banner-icon" />
            <span className="docs-page-banner-text">
              {stats.expSoon > 0 && `${stats.expSoon} document${stats.expSoon === 1 ? '' : 's'} expiring in the next 30 days`}
              {stats.expSoon > 0 && stats.expired > 0 && ' · '}
              {stats.expired > 0 && `${stats.expired} expired`}
            </span>
            <div className="docs-page-banner-actions">
              {stats.expSoon > 0 && (
                <button type="button" onClick={() => setStatus('expiring')}>Show expiring</button>
              )}
              {stats.expired > 0 && (
                <button type="button" onClick={() => setStatus('expired')}>Show expired</button>
              )}
              {(statusFilter === 'expiring' || statusFilter === 'expired') && (
                <button type="button" className="docs-page-banner-clear" onClick={() => setStatus('')}>
                  Clear filter
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Header ── */}
        <div className="docs-page-header">
          <div className="docs-header-left">
            <h1 className="docs-page-title">
              <FontAwesomeIcon icon={faShieldAlt} className="docs-page-title-icon" />
              Documents
            </h1>
            {propertyName !== 'Loading…' && <div className="docs-property-name">{propertyName}</div>}
            {propertyAddress && <div className="docs-property-address">{propertyAddress}</div>}
          </div>
          <button className="docs-upload-btn" onClick={() => setShowUploadModal(true)}>
            <FontAwesomeIcon icon={faPlus} /> Upload Document
          </button>
        </div>

        {/* ── Stat cards ── */}
        <div className="docs-stats-row">
          <div className="docs-stat-card">
            <div className="docs-stat-value">{stats.total}</div>
            <div className="docs-stat-label">Total documents</div>
          </div>
          <div className="docs-stat-card">
            <div className="docs-stat-value" style={{ color: stats.compliant >= 90 ? 'var(--cs-green,#22c55e)' : stats.compliant >= 70 ? 'var(--cs-amber,#f59e0b)' : 'var(--cs-red,#ef4444)' }}>{stats.compliant}%</div>
            <div className="docs-stat-label">Compliance rate</div>
            <div className="docs-stat-bar">
              <div className="docs-stat-bar-fill" style={{ width: `${stats.compliant}%`, background: stats.compliant >= 90 ? 'var(--cs-green,#22c55e)' : stats.compliant >= 70 ? 'var(--cs-amber,#f59e0b)' : 'var(--cs-red,#ef4444)' }} />
            </div>
          </div>
          <div className="docs-stat-card">
            <div className="docs-stat-value" style={{ color: stats.expired > 0 ? 'var(--cs-red,#ef4444)' : undefined }}>{stats.expired}</div>
            <div className="docs-stat-label">Expired</div>
          </div>
          <div className="docs-stat-card">
            <div className="docs-stat-value" style={{ color: stats.expSoon > 0 ? 'var(--cs-amber,#f59e0b)' : undefined }}>{stats.expSoon}</div>
            <div className="docs-stat-label">Expiring soon</div>
          </div>
        </div>

        {/* ── Search bar ── */}
        <div className="docs-search-bar">
          <FontAwesomeIcon icon={faSearch} className="docs-search-icon" />
          <input
            type="text"
            className="docs-search-input"
            placeholder="Search documents…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button className="docs-search-clear" onClick={() => setSearchInput('')}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
          <button className="docs-refresh-btn" onClick={() => { fetchDocuments(); fetchCategories(); }} title="Refresh">
            <FontAwesomeIcon icon={faSyncAlt} />
          </button>
        </div>

        {/* ── Drag-drop hint ── */}
        {dragActive && (
          <div className="docs-dropzone">
            <FontAwesomeIcon icon={faCloudUploadAlt} className="docs-dropzone-icon" />
            <div className="docs-dropzone-text">Drop to upload</div>
            <div className="docs-dropzone-sub">We&apos;ll open the upload form with the file ready</div>
          </div>
        )}

        {/* ── Empty state ── */}
        {documents.length === 0 && (
          <div className="docs-empty-state">
            <FontAwesomeIcon icon={faFolderOpen} className="docs-empty-icon" />
            <div className="docs-empty-title">No documents uploaded yet</div>
            <div className="docs-empty-desc">Click Upload Document to get started.</div>
          </div>
        )}

        {/* ── Category accordions ── */}
        <div className="docs-accordions">
          {categoryAccordions
            .filter(({ docs }) => {
              if (!search) return true;
              const term = search.toLowerCase();
              return docs.some(d => [d.name, d.original_filename, d.summary, d.zone, d.uploaded_by_name, d.type].filter(Boolean).join(' ').toLowerCase().includes(term));
            })
            .map(({ cat, docs, zones }) => {
              const isOpen = openCategories.has(cat.id);
              const colour = cat.colour || '#6b7280';
              const filteredZones = !search ? zones : zones.map(z => ({
                ...z,
                docs: z.docs.filter(d => [d.name, d.original_filename, d.summary, d.zone, d.uploaded_by_name, d.type].filter(Boolean).join(' ').toLowerCase().includes(search.toLowerCase()))
              })).filter(z => z.docs.length > 0);
              const expiredCount = docs.filter(d => getComputedStatus(d) === 'Expired').length;
              const expSoonCount = docs.filter(d => getComputedStatus(d) === 'Expiring Soon').length;
              return (
                <div key={cat.id} className={`docs-accordion ${isOpen ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="docs-accordion-header"
                    style={{ '--acc-color': colour }}
                    onClick={() => toggleCategoryAccordion(cat.id)}
                  >
                    <span className="docs-accordion-icon">
                      <FontAwesomeIcon icon={iconForName(cat.icon)} />
                    </span>
                    <span className="docs-accordion-name">{cat.name}</span>
                    <span className="docs-accordion-count">{docs.length}</span>
                    {expiredCount > 0 && <span className="docs-accordion-alert tone-red">{expiredCount} expired</span>}
                    {expSoonCount > 0 && <span className="docs-accordion-alert tone-amber">{expSoonCount} expiring</span>}
                    <span className="docs-accordion-upload" onClick={e => { e.stopPropagation(); openUploadFor(cat); }} title={`Upload to ${cat.name}`}>
                      <FontAwesomeIcon icon={faPlus} />
                    </span>
                    <FontAwesomeIcon icon={faChevronLeft} className="docs-accordion-chev" />
                  </button>
                  {isOpen && (
                    <div className="docs-accordion-body">
                      {filteredZones.map(({ zone, docs: zoneDocs }) => {
                        const zoneKey = `${cat.id}::${zone}`;
                        const zoneOpen = zone === '__building__' || !openZones.has(zoneKey);
                        return (
                        <div key={zone} className="docs-zone-group">
                          {zone !== '__building__' && (
                            <button
                              type="button"
                              className="docs-zone-label docs-zone-label-toggle"
                              onClick={() => toggleZone(zoneKey)}
                            >
                              <FontAwesomeIcon icon={faLayerGroup} />
                              {zone}
                              <span className="docs-zone-count">{zoneDocs.length}</span>
                              <FontAwesomeIcon icon={faChevronLeft} className={`docs-zone-chev ${zoneOpen ? 'open' : ''}`} />
                            </button>
                          )}
                          {zoneOpen && <div className="docs-list">
                            {zoneDocs.map(doc => (
                              <DocumentRow
                                key={doc.id}
                                doc={doc}
                                isOwner={currentUserId && doc.uploaded_by === currentUserId}
                                isSelected={selected.has(doc.id)}
                              />
                            ))}
                          </div>}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        {/* ── Categories admin (collapsible bottom) ── */}
        <DocumentsCategoriesAdmin
          categories={categories}
          onChange={fetchCategories}
          documents={documents}
          propertyId={propertyId}
          canManage={canManageCats}
          onBulkRecategorised={fetchDocuments}
        />
      </div>

      {/* ── Sticky bulk action bar ── */}
      {selected.size > 0 && (
        <div className="docs-bulk-bar" role="toolbar" aria-label="Bulk actions">
          <span className="docs-bulk-count">{selected.size} selected</span>
          <div className="docs-bulk-spacer" />
          <button type="button" onClick={bulkDownload}>
            <FontAwesomeIcon icon={faDownload} /> Download
          </button>
          <button type="button" onClick={() => setShowMoveModal(true)}>
            <FontAwesomeIcon icon={faFolderOpen} /> Move to category…
          </button>
          <button type="button" className="docs-bulk-danger" onClick={bulkDelete}>
            <FontAwesomeIcon icon={faTrash} /> Delete
          </button>
          <button type="button" className="docs-bulk-clear" onClick={clearSelection} aria-label="Clear selection">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* ── Side panel preview ── */}
      {previewDoc && (
        <DocumentsPreviewPanel
          doc={previewDoc}
          propertyId={propertyId}
          isOwner={currentUserId && previewDoc.uploaded_by === currentUserId}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => handleDownload(previewDoc)}
          onEdit={() => {
            const cat2 = categories.find(c => (previewDoc.category_id && c.id === previewDoc.category_id) || c.name === previewDoc.type);
            setEditingDocument({ ...previewDoc, category_id: cat2?.id || null });
            setShowEditModal(true);
            setPreviewDoc(null);
          }}
          onDelete={() => handleDelete(previewDoc)}
        />
      )}

      {/* ── Move-to-category modal (bulk) ── */}
      {showMoveModal && (
        <div className="docs-modal-overlay" onClick={() => setShowMoveModal(false)}>
          <div className="docs-cat-modal" onClick={e => e.stopPropagation()}>
            <header className="docs-cat-modal-head">
              <h3>Move {selected.size} doc{selected.size === 1 ? '' : 's'} to…</h3>
              <button type="button" className="docs-modal-close" onClick={() => setShowMoveModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </header>
            <div className="docs-cat-modal-body">
              <div className="docs-move-list">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    className="docs-move-row"
                    style={{ '--cat-color': cat.colour || '#6b7280' }}
                    onClick={() => bulkMoveToCategory(cat.id)}
                  >
                    <span className="docs-move-icon">
                      <FontAwesomeIcon icon={iconForName(cat.icon)} />
                    </span>
                    <span className="docs-move-name">{cat.name}</span>
                    <FontAwesomeIcon icon={faChevR} className="docs-move-chev" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Upload wizard modal ── */}
      {showUploadModal && (() => {
        const closeUpload = () => { setShowUploadModal(false); setUploadStep(1); setUploadForm({ name: '', categoryIds: [], type: '', zone: '', summary: '', expiryDate: '', file: null, customZone: '' }); };
        const canNext1 = !!uploadForm.file;
        const canNext2 = uploadForm.categoryIds.length > 0;
        const canNext3 = !!uploadForm.name;
        const selectedCats = categories.filter(c => uploadForm.categoryIds.includes(c.id));
        const stepLabels = ['File', 'Category', 'Details', 'Review'];
        return (
          <div className="docs-modal-overlay" onClick={closeUpload}>
            <div className="docs-modal docs-wizard" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="docs-modal-header">
                <h3 className="docs-modal-title">
                  <FontAwesomeIcon icon={faUpload} className="docs-modal-title-icon" />
                  Upload document
                </h3>
                <button className="docs-modal-close" onClick={closeUpload}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Step indicators */}
              <div className="docs-wizard-steps">
                {stepLabels.map((label, i) => {
                  const n = i + 1;
                  const done = uploadStep > n;
                  const active = uploadStep === n;
                  return (
                    <React.Fragment key={n}>
                      <div className={`docs-wizard-step ${active ? 'active' : ''} ${done ? 'done' : ''}`}>
                        <div className="docs-wizard-step-dot">
                          {done ? <FontAwesomeIcon icon={faCheckCircle} /> : n}
                        </div>
                        <span className="docs-wizard-step-label">{label}</span>
                      </div>
                      {i < stepLabels.length - 1 && <div className={`docs-wizard-connector ${done ? 'done' : ''}`} />}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Step content */}
              <div className="docs-wizard-body">
                {uploadError && (
                  <div className="docs-form-error">
                    <FontAwesomeIcon icon={faExclamationTriangle} /> {uploadError}
                  </div>
                )}

                {/* Step 1: File */}
                {uploadStep === 1 && (
                  <div className="docs-wizard-step-content">
                    <p className="docs-wizard-step-desc">Choose the file you want to upload.</p>
                    <label className="docs-dropzone-pick" htmlFor="docs-file-wiz">
                      {uploadForm.file ? (
                        <div className="docs-dropzone-pick-file">
                          <FontAwesomeIcon icon={faFileAlt} className="docs-dropzone-pick-icon done" />
                          <div className="docs-dropzone-pick-name">{uploadForm.file.name}</div>
                          <div className="docs-dropzone-pick-size">{friendlySize(uploadForm.file.size)}</div>
                          <span className="docs-dropzone-pick-change">Click to change</span>
                        </div>
                      ) : (
                        <div className="docs-dropzone-pick-empty">
                          <FontAwesomeIcon icon={faCloudUploadAlt} className="docs-dropzone-pick-icon" />
                          <div className="docs-dropzone-pick-title">Click to choose a file</div>
                          <div className="docs-dropzone-pick-hint">PDF, DOC, XLS, JPG, PNG · up to 10MB</div>
                        </div>
                      )}
                      <input
                        type="file" id="docs-file-wiz" style={{ display: 'none' }}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                        onChange={(e) => {
                          const f = e.target.files[0];
                          if (f) setUploadForm(prev => ({ ...prev, file: f, name: prev.name || f.name.replace(/\.[^.]+$/, '') }));
                        }}
                      />
                    </label>
                  </div>
                )}

                {/* Step 2: Category + Location */}
                {uploadStep === 2 && (
                  <div className="docs-wizard-step-content">
                    <p className="docs-wizard-step-desc">Where does this document belong?</p>
                    <div className="docs-form-group">
                      <label className="docs-form-label">Category <span className="docs-required">*</span> <span className="docs-form-label-opt">— select all that apply</span></label>
                      <div className="docs-wizard-cat-grid">
                        {categories.map(cat => (
                          <button
                            key={cat.id}
                            type="button"
                            className={`docs-wizard-cat-btn ${uploadForm.categoryIds.includes(cat.id) ? 'selected' : ''}`}
                            style={{ '--cat-color': cat.colour || '#6b7280' }}
                            onClick={() => setUploadForm(f => {
                              const ids = f.categoryIds.includes(cat.id)
                                ? f.categoryIds.filter(id => id !== cat.id)
                                : [...f.categoryIds, cat.id];
                              return { ...f, categoryIds: ids, type: ids.length > 0 ? (categories.find(c => c.id === ids[0])?.name || '') : '' };
                            })}
                          >
                            <span className="docs-wizard-cat-ico"><FontAwesomeIcon icon={iconForName(cat.icon)} /></span>
                            <span className="docs-wizard-cat-name">{cat.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="docs-form-group" style={{ marginTop: 20 }}>
                      <label className="docs-form-label">Location</label>
                      <select
                        className="docs-form-select"
                        value={uploadForm.customZone === '__new__' ? '__new__' : uploadForm.zone}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '__new__') setUploadForm(f => ({ ...f, customZone: '__new__', zone: '' }));
                          else setUploadForm(f => ({ ...f, customZone: '', zone: val }));
                        }}
                      >
                        <option value="">Building-wide</option>
                        {existingZones.map(z => <option key={z} value={z}>{z}</option>)}
                        <option value="Other">Other</option>
                        <option value="__new__">+ Add new floor / level…</option>
                      </select>
                      {uploadForm.customZone === '__new__' && (
                        <input autoFocus type="text" className="docs-form-input docs-zone-custom"
                          placeholder="e.g. Level 10, Ground Floor"
                          value={uploadForm.zone}
                          onChange={(e) => setUploadForm(f => ({ ...f, zone: e.target.value }))} />
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Details */}
                {uploadStep === 3 && (
                  <div className="docs-wizard-step-content">
                    <p className="docs-wizard-step-desc">Add the document details.</p>
                    <div className="docs-form-group">
                      <label className="docs-form-label">Document name <span className="docs-required">*</span></label>
                      <input type="text" className="docs-form-input" autoFocus
                        value={uploadForm.name}
                        onChange={(e) => setUploadForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Fire Risk Assessment 2026" />
                    </div>
                    <div className="docs-form-group">
                      <label className="docs-form-label">Expiry date</label>
                      <div className="docs-expiry-row">
                        <input
                          type="date"
                          className="docs-form-input"
                          disabled={uploadForm.expiryDate === 'N/A'}
                          value={uploadForm.expiryDate === 'N/A' ? '' : uploadForm.expiryDate}
                          onChange={(e) => setUploadForm(f => ({ ...f, expiryDate: e.target.value }))}
                        />
                        <label className="docs-na-toggle">
                          <input
                            type="checkbox"
                            checked={uploadForm.expiryDate === 'N/A'}
                            onChange={(e) => setUploadForm(f => ({ ...f, expiryDate: e.target.checked ? 'N/A' : '' }))}
                          />
                          <span>Not applicable</span>
                        </label>
                      </div>
                    </div>
                    <div className="docs-form-group">
                      <label className="docs-form-label">Notes <span className="docs-form-label-opt">(optional)</span></label>
                      <textarea className="docs-form-textarea" rows="3"
                        value={uploadForm.summary}
                        onChange={(e) => setUploadForm(f => ({ ...f, summary: e.target.value }))}
                        placeholder="Any additional notes about this document" />
                    </div>
                  </div>
                )}

                {/* Step 4: Review */}
                {uploadStep === 4 && (
                  <div className="docs-wizard-step-content">
                    <p className="docs-wizard-step-desc">Everything look right? Hit Upload to confirm.</p>
                    <div className="docs-review-grid">
                      <div className="docs-review-row">
                        <span className="docs-review-label">File</span>
                        <span className="docs-review-value"><FontAwesomeIcon icon={faFileAlt} /> {uploadForm.file?.name} <span className="docs-review-muted">({friendlySize(uploadForm.file?.size)})</span></span>
                      </div>
                      <div className="docs-review-row">
                        <span className="docs-review-label">Category</span>
                        <span className="docs-review-value" style={{ gap: 6, flexWrap: 'wrap' }}>
                          {selectedCats.length === 0 ? '—' : selectedCats.map(c => (
                            <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--cs-bg,#f3f4f6)', borderRadius: 6, padding: '2px 8px', fontSize: '0.8rem' }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.colour || '#6b7280', display: 'inline-block' }} />
                              {c.name}
                            </span>
                          ))}
                        </span>
                      </div>
                      <div className="docs-review-row">
                        <span className="docs-review-label">Location</span>
                        <span className="docs-review-value">{uploadForm.zone || 'Building-wide'}</span>
                      </div>
                      <div className="docs-review-row">
                        <span className="docs-review-label">Name</span>
                        <span className="docs-review-value">{uploadForm.name || '—'}</span>
                      </div>
                      <div className="docs-review-row">
                        <span className="docs-review-label">Expiry</span>
                        <span className="docs-review-value">{uploadForm.expiryDate === 'N/A' ? 'Not applicable' : uploadForm.expiryDate || 'Not set'}</span>
                      </div>
                      {uploadForm.summary && (
                        <div className="docs-review-row">
                          <span className="docs-review-label">Notes</span>
                          <span className="docs-review-value">{uploadForm.summary}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer nav */}
              <div className="docs-wizard-footer">
                {uploadStep > 1 ? (
                  <button type="button" className="docs-btn-cancel" onClick={() => setUploadStep(s => s - 1)}>
                    <FontAwesomeIcon icon={faChevronLeft} /> Back
                  </button>
                ) : (
                  <button type="button" className="docs-btn-cancel" onClick={closeUpload}>Cancel</button>
                )}
                {uploadStep < 4 ? (
                  <button
                    type="button"
                    className="docs-btn-submit"
                    disabled={uploadStep === 1 ? !canNext1 : uploadStep === 2 ? !canNext2 : !canNext3}
                    onClick={() => setUploadStep(s => s + 1)}
                  >
                    Next <FontAwesomeIcon icon={faChevR} />
                  </button>
                ) : (
                  <button type="button" className="docs-btn-submit" disabled={uploading} onClick={handleUpload}>
                    {uploading ? <><FontAwesomeIcon icon={faSpinner} spin /> Uploading…</> : <><FontAwesomeIcon icon={faUpload} /> Upload</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Edit modal ── */}
      {showEditModal && editingDocument && (
        <div className="docs-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="docs-modal docs-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="docs-modal-header">
              <h3 className="docs-modal-title">
                <FontAwesomeIcon icon={faPencilAlt} className="docs-modal-title-icon" /> Edit document
              </h3>
              <button className="docs-modal-close" onClick={() => setShowEditModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleUpdateDocument} className="docs-form">
              <div className="docs-form-row">
                <div className="docs-form-group">
                  <label className="docs-form-label">Category</label>
                  <select
                    className="docs-form-select"
                    value={editingDocument.category_id || ''}
                    onChange={(e) => {
                      const cat = categories.find(c => String(c.id) === e.target.value);
                      setEditingDocument(prev => ({ ...prev, category_id: cat?.id || null, type: cat?.name || prev.type }));
                    }}
                  >
                    <option value="">— Select a category —</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="docs-form-group">
                  <label className="docs-form-label">Location</label>
                  <select
                    className="docs-form-select"
                    value={editingDocument._customZone === '__new__' ? '__new__' : (editingDocument.zone || '')}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '__new__') {
                        setEditingDocument(prev => ({ ...prev, _customZone: '__new__', zone: '' }));
                      } else {
                        setEditingDocument(prev => ({ ...prev, _customZone: '', zone: val }));
                      }
                    }}
                  >
                    <option value="">— Building-wide / N/A —</option>
                    {existingZones.map(z => (
                      <option key={z} value={z}>{z}</option>
                    ))}
                    <option value="Drawings">Drawings</option>
                    <option value="Other">Other</option>
                    <option value="__new__">+ Add new floor / level…</option>
                  </select>
                  {editingDocument._customZone === '__new__' && (
                    <input
                      type="text"
                      className="docs-form-input docs-zone-custom"
                      placeholder="e.g. Level 10, Ground Floor"
                      autoFocus
                      value={editingDocument.zone}
                      onChange={(e) => setEditingDocument(prev => ({ ...prev, zone: e.target.value }))}
                    />
                  )}
                </div>
              </div>
              <div className="docs-form-row">
                <div className="docs-form-group">
                  <label className="docs-form-label">Name <span className="docs-required">*</span></label>
                  <input type="text" className="docs-form-input" required
                    value={editingDocument.name}
                    onChange={(e) => setEditingDocument(prev => ({ ...prev, name: e.target.value }))} />
                </div>
                <div className="docs-form-group">
                  <label className="docs-form-label">Status</label>
                  <select className="docs-form-select"
                    value={editingDocument.status}
                    onChange={(e) => setEditingDocument(prev => ({ ...prev, status: e.target.value }))}>
                    <option value="Active">Active</option>
                    <option value="Expired">Expired</option>
                    <option value="Missing">Missing</option>
                  </select>
                </div>
              </div>
              <div className="docs-form-row">
                <div className="docs-form-group">
                  <label className="docs-form-label">Expiry date</label>
                  <input type="date" className="docs-form-input"
                    value={editingDocument.expiry_date ? String(editingDocument.expiry_date).split('T')[0] : ''}
                    onChange={(e) => setEditingDocument(prev => ({ ...prev, expiry_date: e.target.value }))} />
                  <span className="docs-form-hint">Leave blank if N/A</span>
                </div>
              </div>
              <div className="docs-form-group">
                <label className="docs-form-label">Notes</label>
                <textarea className="docs-form-textarea"
                  value={editingDocument.summary || ''}
                  onChange={(e) => setEditingDocument(prev => ({ ...prev, summary: e.target.value }))} rows="3" />
              </div>
            </form>
            <div className="docs-modal-actions">
              <button type="button" className="docs-btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button type="submit" className="docs-btn-submit" onClick={handleUpdateDocument}>
                <FontAwesomeIcon icon={faPencilAlt} /> Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pinpoint modal ── */}
      {showPinpointModal && pinpointDoc && (
        <div className="docs-modal-overlay" onClick={() => setShowPinpointModal(false)}>
          <div className="docs-modal" onClick={e => e.stopPropagation()}>
            <div className="docs-modal-header">
              <h3><FontAwesomeIcon icon={faMapPin} /> Convert to Pinpoint Drawing</h3>
              <button className="docs-modal-close" onClick={() => setShowPinpointModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleConvertToPinpoint} className="docs-modal-body">
              <div className="docs-pinpoint-banner">
                <FontAwesomeIcon icon={faDrawPolygon} />
                <span>This will enable interactive pin placement on this drawing</span>
              </div>
              <div className="docs-form-row">
                <div className="docs-form-group">
                  <label>Drawing name *</label>
                  <input type="text" required value={pinpointForm.name}
                    onChange={e => setPinpointForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="docs-form-group">
                  <label>Category</label>
                  <select value={pinpointForm.category} onChange={e => setPinpointForm(p => ({ ...p, category: e.target.value }))}>
                    <option value="Roof Safety">Roof Safety</option>
                    <option value="Fire Safety">Fire Safety</option>
                    <option value="Electrical">Electrical</option>
                    <option value="Building Maintenance">Building Maintenance</option>
                    <option value="Equipment Safety">Equipment Safety</option>
                    <option value="General">General</option>
                  </select>
                </div>
              </div>
              <div className="docs-form-group">
                <label>Description (optional)</label>
                <textarea value={pinpointForm.description}
                  onChange={e => setPinpointForm(p => ({ ...p, description: e.target.value }))} rows="2" />
              </div>
              <div className="docs-modal-actions">
                <button type="button" className="docs-btn-cancel" onClick={() => setShowPinpointModal(false)}>Cancel</button>
                <button type="submit" className="docs-btn-submit" disabled={convertingToPinpoint}>
                  {convertingToPinpoint ? <><FontAwesomeIcon icon={faSpinner} spin /> Converting…</> : <><FontAwesomeIcon icon={faMapPin} /> Convert</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
